import { useMutation, useQueryClient } from "@tanstack/react-query";

import companyService from "@services/company.service";
import ToastService from "@services/toast.service";
import {
  ADD_ALTERNATIVE_DOMAIN_MUTATION_KEY,
  COMPANY_QUERY_KEY,
  DELETE_ALTERNATIVE_DOMAIN_MUTATION_KEY,
  UPDATE_ALTERNATIVE_DOMAIN_MUTATION_KEY,
} from "@config/query";

// No optimistic cache updates here: the field keeps the edited row (and its inline
// error) until the request resolves, then refetches on settle. Optimistically adding
// then rolling back would drop the row a failed value needs to stay attached to.

export function useAddAlternativeDomain() {
  const queryClient = useQueryClient();

  return useMutation<void, unknown, { companyId: string; website: string; toast?: string }>({
    mutationKey: [ADD_ALTERNATIVE_DOMAIN_MUTATION_KEY],
    mutationFn: ({ companyId, website }) => companyService.addAlternativeDomain(companyId, website),
    onSuccess: (_data, { toast }) => {
      if (toast) ToastService.showSuccess(toast);
    },
    onSettled: (_data, _error, { companyId }) => {
      void queryClient.invalidateQueries({ queryKey: [COMPANY_QUERY_KEY, companyId] });
    },
  });
}

export function useUpdateAlternativeDomain() {
  const queryClient = useQueryClient();

  return useMutation<
    void,
    unknown,
    { companyId: string; oldWebsite: string; newWebsite: string; toast?: string }
  >({
    mutationKey: [UPDATE_ALTERNATIVE_DOMAIN_MUTATION_KEY],
    mutationFn: ({ companyId, oldWebsite, newWebsite }) =>
      companyService.updateAlternativeDomain(companyId, oldWebsite, newWebsite),
    onSuccess: (_data, { toast }) => {
      if (toast) ToastService.showSuccess(toast);
    },
    onSettled: (_data, _error, { companyId }) => {
      void queryClient.invalidateQueries({ queryKey: [COMPANY_QUERY_KEY, companyId] });
    },
  });
}

export function useDeleteAlternativeDomain() {
  const queryClient = useQueryClient();

  return useMutation<void, unknown, { companyId: string; website: string; toast?: string }>({
    mutationKey: [DELETE_ALTERNATIVE_DOMAIN_MUTATION_KEY],
    mutationFn: ({ companyId, website }) =>
      companyService.deleteAlternativeDomain(companyId, website),
    onSuccess: (_data, { toast }) => {
      if (toast) ToastService.showSuccess(toast);
    },
    onSettled: (_data, _error, { companyId }) => {
      void queryClient.invalidateQueries({ queryKey: [COMPANY_QUERY_KEY, companyId] });
    },
  });
}
